import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Plus, Sparkles, Loader2, MessageSquare, ArrowRight, Clock, CheckCircle2,
} from 'lucide-react';
import type { WorkbenchSessionListItem, WorkbenchStatus } from '../../../types';
import type { AgentSpec } from './types';

/**
 * ONE landing implementation for every agent (sessions list + new
 * conversation). Identical to the proven profiling-agent landing —
 * only identity/copy/routing/api come from `spec`.
 */
const AgentLanding: React.FC<{ spec: AgentSpec }> = ({ spec }) => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [spec.queryKeyPrefix, 'sessions'],
    queryFn: async () => (await spec.api.listSessions({ limit: 50 })).data,
    refetchInterval: 5000,
  });

  const sessions: WorkbenchSessionListItem[] = data?.payload?.sessions ?? [];

  const startNewSession = async () => {
    setCreating(true);
    try {
      const res = await spec.api.createSession({});
      const sid = res.data?.payload?.session_id;
      if (sid) {
        navigate(`${spec.sessionRouteBase}/${sid}`);
      }
    } catch (e) {
      // swallow — user can retry
    } finally {
      setCreating(false);
      refetch();
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="bg-gradient-to-br from-cargo-accent/10 to-cargo-panel border border-cargo-accent/20 rounded-xl p-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-cargo-accent text-xs uppercase tracking-wider font-medium mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            {spec.title}
          </div>
          <h2 className="text-xl font-semibold text-cargo-text mb-1">
            {spec.heroHeading}
          </h2>
          <p className="text-cargo-muted text-sm max-w-xl">
            {spec.heroDescription}
          </p>
        </div>
        <button
          onClick={startNewSession}
          disabled={creating}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-cargo-accent text-white rounded-lg hover:bg-cargo-accent/90 transition-colors font-medium disabled:opacity-60"
        >
          {creating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
          ) : (
            <><Plus className="w-4 h-4" /> New Conversation</>
          )}
        </button>
      </div>

      {/* Session list */}
      <div className="bg-cargo-panel border border-cargo-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-cargo-border flex items-center justify-between">
          <h3 className="text-cargo-text font-semibold text-sm">Recent Conversations</h3>
          <span className="text-cargo-muted text-xs">{data?.payload?.total ?? 0} total</span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-cargo-accent animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-cargo-error text-sm">Failed to load sessions.</div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="w-10 h-10 text-cargo-muted mx-auto mb-3 opacity-60" />
            <p className="text-cargo-text font-medium">No conversations yet</p>
            <p className="text-cargo-muted text-sm mt-1">
              Start one by clicking "New Conversation" above.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-cargo-border">
            {sessions.map((s) => (
              <SessionRow
                key={s.session_id}
                session={s}
                onOpen={() => navigate(`${spec.sessionRouteBase}/${s.session_id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusIndicator: React.FC<{ status: WorkbenchStatus }> = ({ status }) => {
  const map = {
    active:    { icon: MessageSquare, color: 'text-cargo-accent' },
    waiting:   { icon: Loader2,       color: 'text-cargo-warning animate-spin' },
    completed: { icon: CheckCircle2,  color: 'text-cargo-success' },
    failed:    { icon: Clock,         color: 'text-cargo-error' },
  } as const;
  const { icon: Icon, color } = map[status] || map.active;
  return <Icon className={`w-4 h-4 ${color}`} />;
};

const SessionRow: React.FC<{ session: WorkbenchSessionListItem; onOpen: () => void }> = ({ session, onOpen }) => (
  <button
    onClick={onOpen}
    className="w-full flex items-center gap-4 px-4 py-3 hover:bg-cargo-hover/20 transition-colors text-left"
  >
    <div className="flex-shrink-0">
      <StatusIndicator status={session.status} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-cargo-muted truncate">{session.session_id}</span>
        {session.title && (
          <>
            <span className="text-cargo-muted text-xs">·</span>
            <span className="text-cargo-text text-sm truncate">{session.title}</span>
          </>
        )}
      </div>
      <div className="text-cargo-muted text-xs mt-1 truncate">
        {session.last_message_preview || '(empty conversation)'}
      </div>
    </div>
    <div className="flex-shrink-0 text-right">
      <div className="text-cargo-muted text-xs">{session.message_count} message(s)</div>
      <div className="text-cargo-muted text-[10px] mt-0.5">
        {session.updated_at ? new Date(session.updated_at).toLocaleString() : ''}
      </div>
    </div>
    <ArrowRight className="w-4 h-4 text-cargo-muted flex-shrink-0" />
  </button>
);

export default AgentLanding;
